import React, { Component } from 'react'
import { inject } from 'mobx-react'
import { toJS } from 'mobx'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router'
import { Formik, Form, Field } from 'formik'
import assign from 'lodash/assign'
import pick from 'lodash/pick'
import yup from 'yup'

import { invoicingEntityApi, clientContactApi } from '@atheneum/api'

import Button from '@components/Button'
import AddressFields from '@components/Form/AddressFields'
import Input from '@components/Form/Input'
import RichTextEditor from '@components/Form/RichTextEditor'
import { TagsInput } from '@components/Form/Autocomplete'
import FormError from '@components/FormError'
import FormLoader from '@components/FormLoader'
import Spinner from '@components/spinner'

class InvoicingEntityForm extends Component {
  static propTypes = {
    parentAccount: PropTypes.object,
    modalStore: PropTypes.object,
    invoicingEntity: PropTypes.object,
    onSaveSuccess: PropTypes.func
  }

  constructor(props) {
    super(props)
    this.state = {
      error: null,
      loading: true,
      clientOptions: []
    }
    this.getClientOptions()
  }

  getClientOptions = searchString => {
    const { parentAccount } = this.props

    const query = {
      searchString,
      parentAccount: parentAccount.id,
      status: 'active'
    }

    return clientContactApi.getClientContacts(query)
      .then(res => {
        const clients = res.rows.map(client => {
          return {
            id: client.id,
            fullName: client.fullName
          }
        })
        this.setState({ clientOptions: clients, loading: false })
        return clients
      })
      .catch(err => {
        this.setState({ error: err, loading: false })
      })
  }

  submitForm = (invoicingEntityData, actions) => {
    const { projectIdToUpdate } = Object.fromEntries(new URLSearchParams(this.props.location.search))
    this.saveInvoicingEntity({ ...invoicingEntityData, projectIdToUpdate })
      .then(this.props.onSaveSuccess)
      .catch(({ message }) => {
        this.setState({ error: message })
        actions.setSubmitting(false)
      })
  }

  saveInvoicingEntity = invoicingEntityData => {
    return this.props.invoicingEntity
      ? invoicingEntityApi.update(this.props.invoicingEntity, invoicingEntityData)
      : invoicingEntityApi.create(invoicingEntityData)
  }

  getValidationSchema = () => {
    return yup.object().shape({
      parentAccountId: yup.number().integer(),
      legalName: yup.string().required('Legal name is required'),
      vatNumber: yup.string().nullable(),
      glCode: yup.string().nullable(),
      invoicingRegionId: yup
        .number()
        .integer()
        .nullable(),
      comment: yup.string().nullable(),
      address: yup
        .object()
        .shape({
          city: yup.string().required('City is required'),
          countryId: yup
            .number()
            .integer()
            .nullable(),
          address1: yup.string().nullable(),
          address2: yup.string().nullable(),
          state: yup.string().nullable(),
          postalCode: yup.string().nullable(),
          timezoneId: yup
            .number()
            .integer()
            .nullable()
        })
        .required('Address is required'),
      contacts: yup.array().of(
        yup.object().shape({
          id: yup.number().integer()
        })
      )
    })
  }

  render() {
    const { invoicingEntity, parentAccount } = this.props
    if (this.state.loading) return <Spinner />
    return (
      <div>
        <Formik
          initialValues={initializeForm({
            parentAccount,
            invoicingEntity
          })}
          validationSchema={this.getValidationSchema()}
          onSubmit={this.submitForm}
          render={form => (
            <div className='expert-form'>
              <FormLoader loading={form.isSubmitting} />
              <h1 className='expert-form__title'>
                {invoicingEntity
                  ? 'Edit invoicing entity'
                  : 'Create invoicing entity'}
              </h1>
              <FormError error={this.state.error} />
              <Form onSubmit={form.handleSubmit}>
                <div className='expert-form__section'>
                  <h2 className='expert-form__subtitle'>
                    Invoicing entity information
                  </h2>
                  <Field
                    name='legalName'
                    label='Legal name'
                    component={Input}
                  />
                  <Field
                    name='legalEntityAddress'
                    label='Address'
                    component={Input}
                  />
                  <Field
                    name='glCode'
                    label='GL code'
                    short
                    component={Input}
                  />
                  <Field
                    name='vatNumber'
                    label='VAT number'
                    short
                    component={Input}
                  />
                  <Field
                    name='comment'
                    label='Comment'
                    placeholder=''
                    toolbarRight
                    component={RichTextEditor}
                  />
                </div>
                <div className='expert-form__section'>
                  <h2 className='expert-form__subtitle'>
                    Accounting contact information
                  </h2>
                  <Field
                    form={form}
                    name='contacts'
                    label='Client contacts'
                    placeholder='Select client contacts'
                    itemToString={item => item && item.fullName}
                    loadOptions={val => this.getClientOptions(val)}
                    debounce={300}
                    component={TagsInput}
                  />
                </div>
                <div className='expert-form__section'>
                  <h2 className='expert-form__subtitle'>Mailing address</h2>
                  <AddressFields
                    data-cy='addressfields-autocomplete'
                    form={form}
                    name='address'
                    cityLabel='City / Postal code'
                    editAddress
                    timezone
                    secondaryAddress
                    invoicingRegion
                    postalCode
                    invoicingRegionFieldName='invoicingRegionId'
                  />
                </div>
                <div className='expert-form__section expert-form__section--buttons'>
                  <Button
                    primary
                    submit
                    onClick={this.onSubmit}
                    disabled={form.isSubmitting}
                  >
                    Save
                  </Button>
                  <Button
                    onClick={this.props.modalStore.close}
                    disabled={form.isSubmitting}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </div>
          )}
        />
      </div>
    )
  }
}

InvoicingEntityForm.defaultProps = {
  hideButtons: true,
  showCloseIcon: true,
  shouldCloseOnOverlayClick: false
}

export default withRouter(
  inject('modalStore', 'growlStore')(InvoicingEntityForm)
)

function initializeForm({ parentAccount, invoicingEntity }) {
  if (invoicingEntity) {
    return initializeInvoicingEntity(invoicingEntity)
  }

  const { legalName, legalEntityAddress } = Object.fromEntries(new URLSearchParams(window.location.search))

  const initialValues = {
    parentAccountId: parentAccount.id,
    legalName: legalName ?? '',
    legalEntityAddress: legalEntityAddress ?? '',
    vatNumber: '',
    glCode: '',
    invoicingRegionId: null,
    comment: '',
    address: {
      city: '',
      countryId: null,
      address1: '',
      address2: '',
      state: '',
      postalCode: '',
      timezoneId: null
    },
    contacts: []
  }

  return initialValues
}

function initializeInvoicingEntity(invoicingEntity) {
  const address = pick(invoicingEntity.address, [
    'city',
    'countryId',
    'address1',
    'address2',
    'state',
    'postalCode',
    'timezoneId'
  ])

  const initialValues = assign(
    pick(invoicingEntity, [
      'parentAccountId',
      'legalName',
      'legalEntityAddress',
      'vatNumber',
      'glCode',
      'invoicingRegionId',
      'comment'
    ]),
    { contacts: toJS(invoicingEntity.contacts) }
  )

  return assign(initialValues, { address })
}
